import * as XLSX from "xlsx";
import type { Employee, CoverageStatus, MedicalPlan, YesNo } from "../types/form";

export const MAX_EMPLOYEES = 1000;

// Salesforce text-field length limits on Employer_Intake_Census_Member__c.
// A value over these limits is rejected by SF with STRING_TOO_LONG on save,
// which the backend surfaces as a 500 — so we catch them here as per-row
// census errors instead. All other census columns are picklists (validated
// against fixed allowed values below), so they can't overflow.
export const MAX_FIRST_NAME_LENGTH = 40;
export const MAX_LAST_NAME_LENGTH = 80;

const EXPECTED_HEADERS = [
  "Employee Name (Last)",
  "Employee Name (First)",
  "Owner",
  "Medical Coverage",
  "Medical Plan",
  "Dental Plan",
  "Vision",
  "On COBRA or in COBRA Election Period",
];

/**
 * Find the header row and build a column-index → normalized-header map.
 * Handles merged cells by reading each column's value from the header row directly.
 */
function readHeaders(sheet: XLSX.WorkSheet): { headerRow: number; headers: string[] } {
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");
  let headerRow = 0;
  for (let r = range.s.r; r <= Math.min(range.s.r + 5, range.e.r); r++) {
    const cell = sheet[XLSX.utils.encode_cell({ r, c: 0 })];
    if (cell?.v && String(cell.v).trim().toLowerCase().startsWith("employee name")) {
      headerRow = r;
      break;
    }
  }
  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: headerRow, c })];
    headers.push(cell?.v ? String(cell.v).trim() : "");
  }
  return { headerRow, headers };
}

// Header keys that would mutate Object.prototype if assigned to a plain object.
// Defense-in-depth against the xlsx CVE-2023-30533 prototype-pollution class —
// even though we drive everything from EXPECTED_HEADERS, a malicious file could
// inject one of these as an extra header column.
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Convert sheet rows to objects using explicit headers (bypasses merged-cell issues). */
function sheetToRows(sheet: XLSX.WorkSheet, headerRow: number, headers: string[]): Record<string, unknown>[] {
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");
  const rows: Record<string, unknown>[] = [];
  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const row: Record<string, unknown> = Object.create(null);
    let hasValue = false;
    for (let c = 0; c < headers.length; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      const val = cell?.v ?? "";
      const headerKey = headers[c];
      if (headerKey && !FORBIDDEN_KEYS.has(headerKey)) row[headerKey] = val;
      if (val !== "") hasValue = true;
    }
    // Skip sub-header rows (row right after header that has parenthetical hints)
    if (r === headerRow + 1) {
      const firstVal = String(row[headers[0]] ?? "").trim();
      if (firstVal === "" || firstVal.startsWith("(")) continue;
    }
    if (hasValue) {
      row.__excelRow = r + 1; // 1-indexed to match what the user sees in Excel
      rows.push(row);
    }
  }
  return rows;
}

const VALID_YES_NO = ["yes", "no"];

/**
 * Resolve header substrings to the actual row keys present in the sheet.
 * Built once per sheet so per-row field() lookups don't re-scan headers.
 */
function buildHeaderIndex(headers: string[]): Map<string, string> {
  const idx = new Map<string, string>();
  for (const h of headers) {
    if (!h) continue;
    idx.set(h.trim().toLowerCase(), h);
  }
  return idx;
}

/**
 * Look up a row value by header. Preference order:
 *   1. Exact match (case-insensitive, trimmed)
 *   2. Anchored prefix match — handles "Owner (Yes/No)" matching "Owner"
 *   3. Substring fallback — handles templates that wrap headers with extra text
 *
 * Plain substring matching alone caused collisions: searching for "Owner" would
 * non-deterministically match either "Owner" or a later "Owner Eligibility"
 * column depending on Map iteration order.
 */
function field(row: Record<string, unknown>, headerIndex: Map<string, string>, name: string): unknown {
  const target = name.toLowerCase();
  const exact = headerIndex.get(target);
  if (exact !== undefined) return row[exact];
  for (const [normalized, key] of headerIndex) {
    if (normalized.startsWith(target)) return row[key];
  }
  for (const [normalized, key] of headerIndex) {
    if (normalized.includes(target)) return row[key];
  }
  return undefined;
}
const VALID_COVERAGE = ["enrolling", "waiving", "waiting period"];

export interface CensusError {
  row: number;
  name: string;
  column: string;
  value: string;
  expected: string;
}

export interface CensusValidationResult {
  success: boolean;
  employees: Employee[];
  errors: CensusError[];
  errorMessage: string | null;
  totalErrorRows: number;
}

/** Format a list with "or" before the last item: ["A", "B", "C"] → "A, B, or C" */
function formatList(items: string[]): string {
  if (items.length <= 2) return items.join(" or ");
  return `${items.slice(0, -1).join(", ")}, or ${items[items.length - 1]}`;
}

function normalize(val: unknown): string {
  return String(val ?? "").trim();
}

function matchValue<T extends string>(val: string, validValues: string[], mappedValues: T[]): T | null {
  const lower = val.toLowerCase();
  const idx = validValues.indexOf(lower);
  return idx >= 0 ? mappedValues[idx] : null;
}

export interface CensusParseOptions {
  offersDental: boolean;
  offersVision: boolean;
  availablePlans: number;
}

export function parseCensusFile(buffer: ArrayBuffer, options?: CensusParseOptions): CensusValidationResult {
  // CVE-2023-30533: xlsx@0.18.5 has a prototype pollution vulnerability.
  // No patched version on npm (SheetJS moved to a paid model). Mitigated by:
  // 1. All output goes through explicit field extraction (no spread of raw data)
  // 2. sheetToRows builds row objects from known header keys only
  // 3. Employee construction uses named properties, never Object.assign/spread
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(buffer, { type: "array" });
  } catch {
    return {
      success: false,
      employees: [],
      errors: [],
      errorMessage: "Unable to read file. Please ensure it's a valid Excel file and not password-protected.",
      totalErrorRows: 0,
    };
  }

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return {
      success: false,
      employees: [],
      errors: [],
      errorMessage: "No records found in the uploaded file. Please add employee data to the template and re-upload.",
      totalErrorRows: 0,
    };
  }

  const sheet = workbook.Sheets[sheetName];
  const { headerRow, headers } = readHeaders(sheet);

  // Check headers — use includes to handle templates with extra text
  // (e.g. "Medical Coverage (Enrolling, Waiving, Waiting Period)" matches "Medical Coverage")
  const missingHeaders = EXPECTED_HEADERS.filter(
    (h) => !headers.some((fh) => fh.toLowerCase().includes(h.toLowerCase())),
  );
  if (missingHeaders.length > 0) {
    return {
      success: false,
      employees: [],
      errors: [],
      errorMessage: "File content does not match the downloadable template. Please download and use the census template above.",
      totalErrorRows: 0,
    };
  }

  // Read data rows using explicit headers (handles merged cells)
  const dataRows = sheetToRows(sheet, headerRow, headers);
  const headerIndex = buildHeaderIndex(headers);

  if (dataRows.length === 0) {
    return {
      success: false,
      employees: [],
      errors: [],
      errorMessage: "No records found in the uploaded file. Please add employee data to the template and re-upload.",
      totalErrorRows: 0,
    };
  }

  if (dataRows.length > MAX_EMPLOYEES) {
    return {
      success: false,
      employees: [],
      errors: [],
      errorMessage: `The file contains ${dataRows.length} employees, which exceeds the maximum of ${MAX_EMPLOYEES}. Please reduce the number of employees and re-upload.`,
      totalErrorRows: 0,
    };
  }

  const errors: CensusError[] = [];
  const employees: Employee[] = [];

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const rowNum = (row.__excelRow as number) ?? i + 2;
    const lastName = normalize(field(row, headerIndex, "Employee Name (Last)"));
    const firstName = normalize(field(row, headerIndex, "Employee Name (First)"));
    const name = `${firstName} ${lastName}`.trim() || "(missing name)";
    const rowErrors: CensusError[] = [];

    const ownerRaw = normalize(field(row, headerIndex, "Owner"));
    const medCovRaw = normalize(field(row, headerIndex, "Medical Coverage"));
    const medPlanRaw = normalize(field(row, headerIndex, "Medical Plan"));
    const dentalRaw = normalize(field(row, headerIndex, "Dental Plan"));
    const visionRaw = normalize(field(row, headerIndex, "Vision"));
    const cobraRaw = normalize(field(row, headerIndex, "On COBRA"));

    // Required field checks — empty values are errors, not skips
    if (!firstName) rowErrors.push({ row: rowNum, name, column: "Employee Name (First)", value: "", expected: "non-empty text" });
    else if (firstName.length > MAX_FIRST_NAME_LENGTH) rowErrors.push({ row: rowNum, name, column: "Employee Name (First)", value: firstName, expected: `at most ${MAX_FIRST_NAME_LENGTH} characters` });
    if (!lastName) rowErrors.push({ row: rowNum, name, column: "Employee Name (Last)", value: "", expected: "non-empty text" });
    else if (lastName.length > MAX_LAST_NAME_LENGTH) rowErrors.push({ row: rowNum, name, column: "Employee Name (Last)", value: lastName, expected: `at most ${MAX_LAST_NAME_LENGTH} characters` });

    const owner = matchValue<YesNo>(ownerRaw, VALID_YES_NO, ["Yes", "No"]);
    if (!owner) {
      rowErrors.push({ row: rowNum, name, column: "Owner", value: ownerRaw || "(empty)", expected: "Yes or No" });
    }

    const medCov = matchValue<CoverageStatus>(medCovRaw, VALID_COVERAGE, ["Enrolling", "Waiving", "Waiting Period"]);
    if (!medCov) {
      rowErrors.push({ row: rowNum, name, column: "Medical Coverage", value: medCovRaw || "(empty)", expected: formatList(["Enrolling", "Waiving", "Waiting Period"]) });
    }

    // Validate Medical Plan (only required if enrolling + multiple plans).
    // Clamp to [1, 4] — defensive against bad metadata (0 or negative would
    // produce an empty planLabels list and reject every valid plan name).
    const numPlans = Math.max(1, Math.min(4, options?.availablePlans ?? 4));
    const planLabels: MedicalPlan[] = (["Plan 1", "Plan 2", "Plan 3", "Plan 4"] as MedicalPlan[]).slice(0, numPlans);
    const planValues = planLabels.map((p) => p.toLowerCase());
    let medPlan: MedicalPlan = "";
    if (medCov === "Enrolling" && medPlanRaw) {
      const matched = matchValue<MedicalPlan>(medPlanRaw, planValues, planLabels);
      if (!matched) {
        rowErrors.push({ row: rowNum, name, column: "Medical Plan", value: medPlanRaw, expected: formatList(planLabels) });
      } else {
        medPlan = matched;
      }
    }

    // Validate Dental — when not offered, only blank or N/A accepted (forced to N/A)
    const dentalOffered = options?.offersDental ?? true;
    let dental: CoverageStatus | null;
    if (!dentalOffered) {
      // Not offered: blank or N/A → N/A, anything else is an error
      dental = (!dentalRaw || dentalRaw.toLowerCase() === "n/a") ? "N/A" : null;
      if (!dental) {
        rowErrors.push({ row: rowNum, name, column: "Dental Plan", value: dentalRaw, expected: "N/A or blank (dental not offered)" });
      }
    } else {
      dental = matchValue<CoverageStatus>(dentalRaw, VALID_COVERAGE, ["Enrolling", "Waiving", "Waiting Period"]);
      if (!dental) {
        rowErrors.push({ row: rowNum, name, column: "Dental Plan", value: dentalRaw || "(empty)", expected: formatList(["Enrolling", "Waiving", "Waiting Period"]) });
      }
    }

    // Validate Vision — when not offered, only blank or N/A accepted (forced to N/A)
    const visionOffered = options?.offersVision ?? true;
    let vision: CoverageStatus | null;
    if (!visionOffered) {
      vision = (!visionRaw || visionRaw.toLowerCase() === "n/a") ? "N/A" : null;
      if (!vision) {
        rowErrors.push({ row: rowNum, name, column: "Vision", value: visionRaw, expected: "N/A or blank (vision not offered)" });
      }
    } else {
      vision = matchValue<CoverageStatus>(visionRaw, VALID_COVERAGE, ["Enrolling", "Waiving", "Waiting Period"]);
      if (!vision) {
        rowErrors.push({ row: rowNum, name, column: "Vision", value: visionRaw || "(empty)", expected: formatList(["Enrolling", "Waiving", "Waiting Period"]) });
      }
    }

    const cobra = matchValue<YesNo>(cobraRaw, VALID_YES_NO, ["Yes", "No"]);
    if (!cobra) {
      rowErrors.push({ row: rowNum, name, column: "On COBRA or in COBRA Election Period", value: cobraRaw || "(empty)", expected: "Yes or No" });
    }

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
    } else {
      employees.push({
        firstName,
        lastName,
        owner: owner ?? "",
        medicalCoverage: medCov ?? "",
        medicalPlan: medPlan,
        dentalCoverage: dental ?? "",
        visionCoverage: vision ?? "",
        cobra: cobra ?? "",
        source: "upload",
      });
    }
  }

  if (errors.length > 0) {
    const errorRowCount = new Set(errors.map((e) => e.row)).size;
    return {
      success: false,
      employees: [],
      errors,
      errorMessage: null,
      totalErrorRows: errorRowCount,
    };
  }

  return { success: true, employees, errors: [], errorMessage: null, totalErrorRows: 0 };
}
